import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { User, Camera, FloppyDisk, Ruler, Scales, Bell, BellSlash, X, IdentificationCard, ArrowsLeftRight, Plus, Buildings, ShieldCheck, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { isPushSupported, getSubscriptionState, enablePush, disablePush } from "@/lib/webPush";
import { GuardianConsentRequests, ConsentRecords } from "@/components/GuardianConsent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_META = {
  athlete:         { label: "Athlete",           desc: "Train, log workouts & track readiness" },
  general_client:  { label: "General Fitness",   desc: "Personal fitness & habit tracking" },
  parent:          { label: "Parent / Guardian", desc: "Follow your athlete's progress" },
  strength_coach:  { label: "S&C Coach",         desc: "Coach clients with programming tools" },
  fitness_trainer: { label: "Fitness Trainer",   desc: "Coach general-fitness clients" },
  sports_coach:    { label: "Sports Coach",      desc: "Coach sport-specific athletes" },
  academy_admin:   { label: "Academy Admin",     desc: "Manage your academy & staff" },
  super_admin:     { label: "Super Admin",       desc: "Platform administration" },
};
const roleLabel = (r) => ROLE_META[r]?.label || r;

// Curated IANA timezones spanning common coach regions (plus the browser's own).
const TIMEZONES = Array.from(new Set([
  Intl.DateTimeFormat().resolvedOptions().timeZone,
  "UTC",
  "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York",
  "America/Sao_Paulo", "Europe/London", "Europe/Paris", "Europe/Berlin",
  "Europe/Moscow", "Africa/Johannesburg", "Asia/Dubai", "Asia/Kolkata",
  "Asia/Singapore", "Asia/Shanghai", "Asia/Tokyo", "Australia/Sydney", "Pacific/Auckland",
].filter(Boolean)));


export default function Profile() {
  const { user, setUser, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", bio: "", height_cm: "", weight_kg: "", units: "imperial", timezone: "UTC" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState([]); // [{kind, label, enabled}]
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [pushState, setPushState] = useState({ supported: false, subscribed: false, permission: "default" });
  const [pushBusy, setPushBusy] = useState(false);
  const [quiet, setQuiet] = useState({ enabled: false, start_hour: 22, end_hour: 6, timezone: "UTC", quiet_days: [], match_days: [] });
  const [quietLoaded, setQuietLoaded] = useState(false);
  const [quietSaving, setQuietSaving] = useState(false);
  const [newMatchDate, setNewMatchDate] = useState("");
  // Profile (role) management
  const [profiles, setProfiles] = useState(null); // { active_role, profiles[], addable[], owns_academy, academy }
  const [profileBusy, setProfileBusy] = useState(false);
  const [roleToAdd, setRoleToAdd] = useState("");
  const [confirmSwitch, setConfirmSwitch] = useState(null); // { role, notes[] }
  // Billing + account data/privacy
  const [proStatus, setProStatus] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [delPassword, setDelPassword] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get("/pro/status").then(({ data }) => setProStatus(data)).catch(() => {});
  }, []);

  const exportMyData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `edge-training-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded");
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const deleteMyAccount = async () => {
    setDeleting(true);
    try {
      await api.post("/me/delete", { password: delPassword || null, confirm: delConfirm });
      toast.success("Your account has been deleted");
      await logout();
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete account");
    } finally {
      setDeleting(false);
    }
  };

  const loadProfiles = async () => {
    try {
      const { data } = await api.get("/profiles/me");
      setProfiles(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadProfiles(); }, []);

  const applySwitch = async (role, confirm = false) => {
    setProfileBusy(true);
    try {
      const { data } = await api.post("/profiles/switch", { role, confirm });
      if (data.needs_confirmation) {
        setConfirmSwitch({ role: data.target_role, notes: data.impact?.notes || [] });
        return;
      }
      setConfirmSwitch(null);
      if (data.user) setUser(data.user);
      await checkAuth();
      await loadProfiles();
      toast.success(`Switched to ${roleLabel(role)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch profile");
    } finally {
      setProfileBusy(false);
    }
  };

  const addProfile = async () => {
    if (!roleToAdd) return;
    setProfileBusy(true);
    try {
      await api.post("/profiles/add", { role: roleToAdd });
      toast.success(`${roleLabel(roleToAdd)} profile added`);
      setRoleToAdd("");
      await loadProfiles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add profile");
    } finally {
      setProfileBusy(false);
    }
  };


  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        bio: user.bio || "",
        height_cm: user.height_cm || "",
        weight_kg: user.weight_kg || "",
        units: user.units || "imperial",
        timezone: user.timezone || (Intl.DateTimeFormat().resolvedOptions().timeZone) || "UTC",
      });
    }
  }, [user]);

  useEffect(() => {
    let alive = true;
    api.get("/notifications/preferences").then(({ data }) => {
      if (alive) setNotifPrefs(data?.kinds || []);
    }).catch(() => {}).finally(() => { if (alive) setPrefsLoading(false); });
    return () => { alive = false; };
  }, []);

  // Fetch current browser push state (supported / permission / subscribed).
  useEffect(() => {
    let alive = true;
    if (!isPushSupported()) {
      setPushState({ supported: false, subscribed: false, permission: "denied" });
      return () => {};
    }
    getSubscriptionState().then((s) => { if (alive) setPushState(s); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const togglePush = async () => {
    if (!pushState.supported) {
      toast.error("This browser doesn't support push notifications");
      return;
    }
    setPushBusy(true);
    try {
      if (pushState.subscribed) {
        await disablePush();
        setPushState((s) => ({ ...s, subscribed: false }));
        toast.success("Browser push disabled");
      } else {
        await enablePush();
        setPushState({ supported: true, subscribed: true, permission: "granted" });
        toast.success("Browser push enabled — you'll get alerts even when the tab is closed");
      }
    } catch (e) {
      toast.error(e?.message || "Could not toggle push");
    } finally {
      setPushBusy(false);
    }
  };

  // Load quiet hours + auto-detect browser timezone the first time.
  useEffect(() => {
    let alive = true;
    api.get("/notifications/quiet-hours").then(({ data }) => {
      if (!alive) return;
      let tz = data?.timezone || "UTC";
      // If user has never set a tz (still UTC default), auto-fill from browser.
      if (tz === "UTC" && typeof Intl !== "undefined") {
        try {
          const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (browserTz && browserTz !== "UTC") tz = browserTz;
        } catch (e) { /* keep UTC */ }
      }
      setQuiet({
        enabled: !!data?.enabled,
        start_hour: Number(data?.start_hour ?? 22),
        end_hour: Number(data?.end_hour ?? 6),
        timezone: tz,
        quiet_days: Array.isArray(data?.quiet_days) ? data.quiet_days.map(Number) : [],
        match_days: Array.isArray(data?.match_days) ? data.match_days : [],
      });
    }).catch(() => {}).finally(() => { if (alive) setQuietLoaded(true); });
    return () => { alive = false; };
  }, []);

  const saveQuiet = async (nextValue) => {
    setQuietSaving(true);
    const payload = nextValue ?? quiet;
    try {
      await api.put("/notifications/quiet-hours", payload);
      setQuiet(payload);
      toast.success(payload.enabled ? "Quiet hours updated" : "Quiet hours disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save quiet hours");
    } finally {
      setQuietSaving(false);
    }
  };

  const formatHour = (h) => {
    const hh = ((h % 12) === 0) ? 12 : (h % 12);
    return `${hh}:00 ${h < 12 ? "AM" : "PM"}`;
  };

  // Deep-link support: /profile#notifications scrolls the prefs card into view.
  useEffect(() => {
    if (prefsLoading) return;
    if (typeof window === "undefined") return;
    if (window.location.hash === "#notifications") {
      const el = document.getElementById("notifications");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [prefsLoading]);

  const toggleKind = async (kind, nextEnabled) => {
    const optimistic = notifPrefs.map((p) => p.kind === kind ? { ...p, enabled: nextEnabled } : p);
    setNotifPrefs(optimistic);
    setPrefsSaving(true);
    try {
      const muted = optimistic.filter((p) => !p.enabled).map((p) => p.kind);
      await api.put("/notifications/preferences", { muted });
      toast.success(nextEnabled ? "Unmuted" : "Muted");
    } catch {
      // rollback
      setNotifPrefs(notifPrefs);
      toast.error("Could not save preference");
    } finally {
      setPrefsSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name || undefined,
        bio: form.bio || undefined,
        height_cm: form.height_cm ? Number(form.height_cm) : undefined,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : undefined,
        units: form.units,
        timezone: form.timezone || undefined,
      };
      const { data } = await api.patch("/profile", payload);
      setUser(data);
      toast.success("Profile updated.");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max 5MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser({ ...user, picture: data.url });
      toast.success("Avatar updated.");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const avatarSrc = user?.picture
    ? user.picture.startsWith("/api/") ? `${BACKEND_URL}${user.picture}` : user.picture
    : null;

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="profile-page">
      <div className="mb-8">
        <div className="label-tiny text-brand-accent mb-2">// IDENTITY</div>
        <h1 className="font-heading text-4xl sm:text-5xl">PROFILE</h1>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1">
          <div className="tactical-card p-6 text-center">
            <div className="relative inline-block mb-4">
              {avatarSrc ? (
                <img src={avatarSrc} alt="" className="w-32 h-32 rounded-full object-cover border-2 border-brand-accent" data-testid="profile-avatar" />
              ) : (
                <div className="w-32 h-32 rounded-full bg-brand-primary flex items-center justify-center text-4xl font-heading" data-testid="profile-avatar-placeholder">
                  {user?.name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-brand-accent text-surface-base flex items-center justify-center hover:scale-110 transition-transform disabled:opacity-50"
                data-testid="upload-avatar-button"
              >
                <Camera size={18} weight="fill" />
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAvatar} data-testid="avatar-file-input" />
            </div>
            <h2 className="font-heading text-xl">{user?.name?.toUpperCase()}</h2>
            <p className="text-xs text-text-secondary mt-1">{user?.email}</p>
            {uploading && <div className="label-tiny mt-3 text-brand-accent">UPLOADING...</div>}
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="tactical-card p-6 space-y-4">
            <h3 className="font-heading text-lg mb-2 flex items-center gap-2">
              <User size={18} weight="duotone" className="text-brand-accent" /> ATHLETE DATA
            </h3>
            <div>
              <label className="label-tiny block mb-2">NAME</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-surface-elevated border-white/10" data-testid="profile-name-input" />
            </div>
            <div>
              <label className="label-tiny block mb-2">BIO</label>
              <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Tell the squad about your journey..." className="bg-surface-elevated border-white/10" data-testid="profile-bio-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-tiny block mb-2 flex items-center gap-1"><Ruler size={12} /> HEIGHT (CM)</label>
                <Input type="number" value={form.height_cm} onChange={(e) => setForm({ ...form, height_cm: e.target.value })} className="bg-surface-elevated border-white/10" data-testid="profile-height-input" />
              </div>
              <div>
                <label className="label-tiny block mb-2 flex items-center gap-1"><Scales size={12} /> WEIGHT (KG)</label>
                <Input type="number" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} className="bg-surface-elevated border-white/10" data-testid="profile-weight-input" />
              </div>
            </div>
            <div>
              <label className="label-tiny block mb-2">UNITS</label>
              <Select value={form.units} onValueChange={(v) => setForm({ ...form, units: v })}>
                <SelectTrigger className="bg-surface-elevated border-white/10" data-testid="profile-units-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface-DEFAULT border-white/10 text-white">
                  <SelectItem value="imperial">Imperial (lbs / inches)</SelectItem>
                  <SelectItem value="metric">Metric (kg / cm)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-tiny block mb-2">TIMEZONE</label>
              <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                <SelectTrigger className="bg-surface-elevated border-white/10" data-testid="profile-timezone-select"><SelectValue placeholder="Select timezone" /></SelectTrigger>
                <SelectContent className="bg-surface-DEFAULT border-white/10 text-white max-h-72">
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz} value={tz}>{tz.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-text-secondary mt-1.5">Your coach dashboard rolls over "today" at your local midnight.</p>
            </div>
            <button onClick={save} disabled={saving} className="btn-kinetic w-full py-3 text-xs label-tiny tracking-widest flex items-center justify-center gap-2 disabled:opacity-50" data-testid="profile-save-button">
              <FloppyDisk size={14} weight="bold" /> {saving ? "SAVING..." : "SAVE PROFILE"}
            </button>
          </div>

          {/* Profiles / roles management */}
          <div className="tactical-card p-6 space-y-4 mt-6" data-testid="profiles-card">
            <h3 className="font-heading text-lg flex items-center gap-2">
              <IdentificationCard size={18} weight="duotone" className="text-brand-accent" /> PROFILES
            </h3>
            <p className="text-xs text-text-secondary">
              Choose your starting profile or switch between the profiles you use. Switching only changes
              which dashboard and tools you see — your data, history and connections are always preserved.
            </p>

            {!profiles ? (
              <div className="label-tiny text-text-secondary" data-testid="profiles-loading">Loading…</div>
            ) : (
              <>
                <div className="space-y-2" data-testid="profiles-list">
                  {profiles.profiles.map((r) => {
                    const active = r === profiles.active_role;
                    return (
                      <div
                        key={r}
                        className={`flex items-center justify-between gap-3 border px-4 py-3 ${active ? "border-brand-accent bg-brand-accent/10" : "border-white/10 bg-surface-elevated/40"}`}
                        data-testid={`profile-item-${r}`}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-medium flex items-center gap-2">
                            {roleLabel(r)}
                            {active && <span className="label-tiny text-[10px] text-brand-accent inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> ACTIVE</span>}
                          </div>
                          <div className="text-[11px] text-text-secondary mt-0.5">{ROLE_META[r]?.desc}</div>
                        </div>
                        {!active && (
                          <button
                            onClick={() => applySwitch(r)}
                            disabled={profileBusy}
                            data-testid={`profile-switch-${r}`}
                            className="px-3 py-1.5 text-[11px] label-tiny tracking-widest border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-white transition-colors disabled:opacity-50 inline-flex items-center gap-1.5 flex-shrink-0"
                          >
                            <ArrowsLeftRight size={13} weight="bold" /> SWITCH
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {profiles.addable.length > 0 && (
                  <div className="flex items-end gap-2 pt-1" data-testid="profile-add-row">
                    <div className="flex-1">
                      <label className="label-tiny block mb-2">ADD A PROFILE</label>
                      <Select value={roleToAdd} onValueChange={setRoleToAdd}>
                        <SelectTrigger className="bg-surface-elevated border-white/10" data-testid="profile-add-select">
                          <SelectValue placeholder="Choose a profile to add…" />
                        </SelectTrigger>
                        <SelectContent className="bg-surface-DEFAULT border-white/10 text-white">
                          {profiles.addable.map((r) => (
                            <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <button
                      onClick={addProfile}
                      disabled={profileBusy || !roleToAdd}
                      data-testid="profile-add-button"
                      className="px-4 py-2.5 text-[11px] label-tiny tracking-widest border border-white/20 text-white hover:border-brand-accent transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
                    >
                      <Plus size={13} weight="bold" /> ADD
                    </button>
                  </div>
                )}

                {/* Academy / facility upgrade — reuses the existing academy setup flow */}
                <div className="border-t border-white/10 pt-4">
                  {profiles.owns_academy ? (
                    <button
                      onClick={() => navigate("/academy")}
                      data-testid="academy-manage-button"
                      className="w-full py-3 text-xs label-tiny tracking-widest border border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-white transition-colors inline-flex items-center justify-center gap-2"
                    >
                      <Buildings size={14} weight="duotone" /> MANAGE {profiles.academy?.name ? profiles.academy.name.toUpperCase() : "ACADEMY"}
                    </button>
                  ) : (
                    <div className="border border-white/10 bg-white/[0.02] p-4 text-center">
                      <Buildings size={20} weight="duotone" className="text-brand-accent mx-auto mb-2" />
                      <p className="text-[11px] text-text-secondary">
                        Academies are set up by the Edge Training team. Want to run your gym, club or
                        team on Edge?{" "}
                        <a href="mailto:admin@edgetraining.com" className="text-brand-accent underline">
                          Contact us
                        </a>{" "}
                        and we&apos;ll provision your academy and invite you as its admin.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="tactical-card p-6 space-y-4 mt-6" id="notifications" data-testid="notification-preferences-card">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg flex items-center gap-2">
                <Bell size={18} weight="duotone" className="text-brand-accent" /> NOTIFICATIONS
              </h3>
              {prefsSaving && <span className="label-tiny text-[10px] text-brand-accent">SAVING…</span>}
            </div>
            <p className="text-xs text-text-secondary">
              Choose which alerts light up your bell. Muted kinds are silently dropped —
              you can still see your inbox via Messages.
            </p>

            <div className="border border-white/10 bg-surface-elevated/40 px-4 py-3 flex items-center justify-between gap-4" data-testid="browser-push-row">
              <div className="min-w-0">
                <div className="text-sm font-medium flex items-center gap-2">
                  {pushState.subscribed ? <Bell size={14} weight="fill" className="text-brand-heat" /> : <BellSlash size={14} className="text-text-secondary" />}
                  Browser push
                </div>
                <div className="text-[11px] text-text-secondary mt-0.5">
                  {!pushState.supported
                    ? "Not supported in this browser"
                    : pushState.subscribed
                      ? "OS-level alerts on. You'll see notifications even with the tab closed."
                      : "Enable OS-level alerts so you never miss a coach ping."}
                </div>
              </div>
              <button
                onClick={togglePush}
                disabled={pushBusy || !pushState.supported}
                className={`shrink-0 border px-3 py-2 text-[11px] label-tiny tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  pushState.subscribed
                    ? "border-white/20 text-text-secondary hover:!border-red-400 hover:!text-red-400"
                    : "border-brand-heat text-brand-heat hover:!bg-brand-heat hover:!text-white"
                }`}
                data-testid="browser-push-toggle"
              >
                {pushBusy ? "..." : pushState.subscribed ? "DISABLE" : "ENABLE"}
              </button>
            </div>

            <div className="border border-white/10 bg-surface-elevated/40 px-4 py-3 space-y-3" data-testid="quiet-hours-row">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-medium">Quiet hours</div>
                  <div className="text-[11px] text-text-secondary mt-0.5">
                    {quiet.enabled
                      ? (quiet.quiet_days?.length > 0
                          ? `Push muted ${formatHour(quiet.start_hour)} → ${formatHour(quiet.end_hour)} nightly plus full days: ${quiet.quiet_days.map((d) => ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d]).join(", ")} (${quiet.timezone}).`
                          : `Push muted from ${formatHour(quiet.start_hour)} → ${formatHour(quiet.end_hour)} (${quiet.timezone}). Digest fires when the window ends.`)
                      : "Hold overnight pushes and land them as a digest in the morning."}
                  </div>
                </div>
                <Switch
                  checked={quiet.enabled}
                  onCheckedChange={(v) => saveQuiet({ ...quiet, enabled: v })}
                  disabled={!quietLoaded || quietSaving}
                  data-testid="quiet-hours-switch"
                />
              </div>
              {quiet.enabled && (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="label-tiny text-[10px] block mb-1">START</label>
                      <Select
                        value={String(quiet.start_hour)}
                        onValueChange={(v) => saveQuiet({ ...quiet, start_hour: Number(v) })}
                      >
                        <SelectTrigger className="bg-surface-elevated border-white/10 h-9" data-testid="quiet-hours-start"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-surface-DEFAULT border-white/10 text-white max-h-60">
                          {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                            <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="label-tiny text-[10px] block mb-1">END</label>
                      <Select
                        value={String(quiet.end_hour)}
                        onValueChange={(v) => saveQuiet({ ...quiet, end_hour: Number(v) })}
                      >
                        <SelectTrigger className="bg-surface-elevated border-white/10 h-9" data-testid="quiet-hours-end"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-surface-DEFAULT border-white/10 text-white max-h-60">
                          {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                            <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="label-tiny text-[10px] block mb-1">TIMEZONE</label>
                      <Input
                        value={quiet.timezone}
                        onChange={(e) => setQuiet({ ...quiet, timezone: e.target.value })}
                        onBlur={() => { if (quiet.timezone) saveQuiet(quiet); }}
                        placeholder="America/New_York"
                        className="bg-surface-elevated border-white/10 h-9 text-xs"
                        data-testid="quiet-hours-tz"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="label-tiny text-[10px]">FULL-DAY QUIET</label>
                      <button
                        type="button"
                        onClick={() => saveQuiet({ ...quiet, quiet_days: quiet.quiet_days.includes(0) ? quiet.quiet_days.filter((d) => d !== 0) : [...quiet.quiet_days, 0].sort((a, b) => a - b) })}
                        className={`text-[10px] label-tiny tracking-widest px-2 py-0.5 border transition-colors ${
                          quiet.quiet_days.includes(0)
                            ? "border-brand-heat text-brand-heat bg-brand-heat/10"
                            : "border-white/20 text-text-secondary hover:!border-brand-heat hover:!text-brand-heat"
                        }`}
                        data-testid="quiet-hours-preset-sunday"
                        title="Preset: silence all Sunday"
                      >
                        SUNDAY OFF
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-testid="quiet-days-picker">
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label, dow) => {
                        const active = quiet.quiet_days.includes(dow);
                        return (
                          <button
                            key={dow}
                            type="button"
                            onClick={() => {
                              const next = active
                                ? quiet.quiet_days.filter((d) => d !== dow)
                                : [...quiet.quiet_days, dow].sort((a, b) => a - b);
                              saveQuiet({ ...quiet, quiet_days: next });
                            }}
                            className={`flex-1 min-w-[42px] py-1.5 text-[11px] label-tiny tracking-wider border transition-colors ${
                              active
                                ? "bg-brand-heat/15 border-brand-heat text-brand-heat"
                                : "border-white/15 text-text-secondary hover:!text-white hover:!border-white/40"
                            }`}
                            data-testid={`quiet-day-${dow}`}
                            aria-pressed={active}
                          >
                            {label.toUpperCase()}
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-[10px] text-text-secondary mt-1">
                      Tapped days are silent 24 hours. Great for keeping Sunday completely notification-free.
                    </div>
                  </div>

                  <div data-testid="match-days-section">
                    <label className="label-tiny text-[10px] block mb-2">MATCH DAYS · OVERRIDE</label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="date"
                        value={newMatchDate}
                        min={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setNewMatchDate(e.target.value)}
                        className="bg-surface-elevated border border-white/15 px-3 py-1.5 text-xs text-slate-800 h-9"
                        data-testid="match-day-input"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (!newMatchDate) return;
                          if (quiet.match_days.includes(newMatchDate)) {
                            toast.info("Already added");
                            setNewMatchDate("");
                            return;
                          }
                          saveQuiet({ ...quiet, match_days: [...quiet.match_days, newMatchDate].sort() });
                          setNewMatchDate("");
                        }}
                        disabled={!newMatchDate || quietSaving}
                        className="px-3 py-1.5 h-9 border border-brand-heat text-brand-heat text-[11px] label-tiny tracking-widest hover:!bg-brand-heat hover:!text-white transition-colors disabled:opacity-50"
                        data-testid="match-day-add"
                      >
                        ADD
                      </button>
                    </div>
                    {quiet.match_days.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="match-days-list">
                        {quiet.match_days.map((d) => (
                          <span
                            key={d}
                            className="inline-flex items-center gap-1.5 border border-brand-heat/50 bg-brand-heat/10 text-brand-heat px-2 py-1 text-[11px] label-tiny tracking-wider"
                            data-testid={`match-day-${d}`}
                          >
                            {new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                            <button
                              type="button"
                              onClick={() => saveQuiet({ ...quiet, match_days: quiet.match_days.filter((x) => x !== d) })}
                              className="hover:!text-white transition-colors"
                              aria-label={`Remove ${d}`}
                              data-testid={`match-day-remove-${d}`}
                            >
                              <X size={10} weight="bold" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="text-[10px] text-text-secondary mt-1.5">
                      Match days let every push through — even during quiet hours or Sunday off.
                    </div>
                  </div>
                </>
              )}
            </div>
            {prefsLoading ? (
              <div className="text-sm text-text-secondary" data-testid="notification-prefs-loading">Loading…</div>
            ) : (
              <div className="divide-y divide-white/5 border border-white/5">
                {notifPrefs.map((p) => (
                  <div key={p.kind} className="flex items-center justify-between px-4 py-3" data-testid={`notification-pref-row-${p.kind}`}>
                    <div>
                      <div className="text-sm font-medium">{p.label}</div>
                      <div className="text-[11px] text-text-secondary label-tiny tracking-wider">// {p.kind.toUpperCase()}</div>
                    </div>
                    <Switch
                      checked={p.enabled}
                      onCheckedChange={(v) => toggleKind(p.kind, v)}
                      disabled={prefsSaving}
                      data-testid={`notification-pref-switch-${p.kind}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Guardian consent requests (shown only when the caller has pending requests) */}
      <div className="mt-6 grid md:grid-cols-2 gap-6" data-testid="guardian-consent-section">
        <GuardianConsentRequests />
        <ConsentRecords />
      </div>

      {/* Billing & Plan */}
      <div className="mt-6 grid md:grid-cols-2 gap-6" data-testid="account-billing-privacy">
        <div className="tactical-card p-6 space-y-4" data-testid="billing-card">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} weight="duotone" className="text-brand-accent" />
            <h3 className="font-heading text-lg">BILLING & PLAN</h3>
          </div>
          {(() => {
            const isCoach = !!proStatus?.is_coach_pro;
            const activePaid = proStatus?.is_pro || proStatus?.is_coach_pro;
            const until = proStatus?.coach_pro_until || proStatus?.pro_until;
            const untilStr = until
              ? new Date(until).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
              : null;
            const planLabel = !activePaid
              ? "Free"
              : isCoach
                ? (proStatus?.coach_tier === "coach_plus" ? "Coach Plus" : "Coach Pro")
                : "Edge Training Pro";
            return (
              <>
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="label-tiny text-text-secondary">CURRENT PLAN</div>
                    <div className="font-heading text-2xl" data-testid="billing-plan-label">{planLabel}</div>
                  </div>
                  <span className={`label-tiny px-2 py-1 border ${activePaid ? "border-green-500/40 text-green-400" : "border-white/15 text-text-secondary"}`}>
                    {activePaid ? "ACTIVE" : "FREE TIER"}
                  </span>
                </div>
                {isCoach && (
                  <div className="text-sm text-text-secondary" data-testid="billing-client-usage">
                    Clients: <span className="text-white">{proStatus?.client_count ?? 0}</span>
                    {proStatus?.client_limit != null && <span> / {proStatus.client_limit}</span>}
                  </div>
                )}
                {activePaid && untilStr && (
                  <div className="text-sm" data-testid="billing-renewal">
                    <span className="text-text-secondary">Access valid until </span>
                    <span className="text-white font-medium">{untilStr}</span>
                  </div>
                )}
                <div className="text-[11px] leading-snug text-text-secondary border-t border-white/5 pt-3">
                  {activePaid ? (
                    <>Plans are fixed-term and <b className="text-white/80">do not auto-renew</b>. To cancel, simply
                    let it lapse — on {untilStr || "the end date"} your account reverts to Free. Your athlete
                    records and training history are never deleted when a plan ends.</>
                  ) : (
                    <>You are on the Free tier. Upgrading unlocks Pro features for a fixed term with no auto-renewal.</>
                  )}
                </div>
                <button
                  onClick={() => navigate("/upgrade")}
                  className="btn-kinetic w-full py-2.5 text-xs label-tiny tracking-widest"
                  data-testid="billing-manage-button"
                >
                  {activePaid ? "VIEW PLANS" : "UPGRADE"}
                </button>
              </>
            );
          })()}
        </div>

        {/* Your Data & Privacy */}
        <div className="tactical-card p-6 space-y-4" data-testid="data-privacy-card">
          <div className="flex items-center gap-2">
            <IdentificationCard size={18} weight="duotone" className="text-brand-accent" />
            <h3 className="font-heading text-lg">YOUR DATA & PRIVACY</h3>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium">Download my data</div>
            <div className="text-[11px] text-text-secondary leading-snug">
              Get a JSON copy of your own account, training and progress data. Never includes passwords,
              tokens or other people's information.
            </div>
            <button
              onClick={exportMyData}
              disabled={exporting}
              className="mt-2 border border-white/15 hover:border-brand-accent hover:text-brand-accent transition-colors px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50"
              data-testid="export-data-button"
            >
              {exporting ? "PREPARING…" : "DOWNLOAD MY DATA"}
            </button>
          </div>
          <div className="border-t border-white/5 pt-4 space-y-1">
            <div className="text-sm font-medium text-red-400">Delete my account</div>
            <div className="text-[11px] text-text-secondary leading-snug">
              Permanently removes your account and personal data, and detaches you from coaches, teams and
              guardians. Shared academy records are preserved. This cannot be undone.
            </div>
            <button
              onClick={() => { setShowDelete(true); setDelPassword(""); setDelConfirm(""); }}
              className="mt-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors px-4 py-2 text-xs label-tiny tracking-widest"
              data-testid="delete-account-button"
            >
              DELETE ACCOUNT
            </button>
          </div>
          <div className="flex items-center gap-4 pt-2 label-tiny text-text-secondary">
            <a href="/privacy" target="_blank" rel="noreferrer" className="hover:text-brand-accent transition-colors" data-testid="profile-privacy-link">PRIVACY</a>
            <a href="/terms" target="_blank" rel="noreferrer" className="hover:text-brand-accent transition-colors" data-testid="profile-terms-link">TERMS</a>
          </div>
        </div>
      </div>

      {/* Delete account confirmation */}
      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="delete-account-modal">
          <div className="tactical-card p-6 max-w-md w-full space-y-4">
            <div className="flex items-start gap-3">
              <Warning size={24} weight="fill" className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-heading text-lg leading-tight">DELETE YOUR ACCOUNT?</h3>
                <p className="text-xs text-text-secondary mt-1">
                  This permanently deletes your personal data and cannot be undone. Shared academy records are preserved.
                </p>
              </div>
            </div>
            <div>
              <label className="label-tiny block mb-1">PASSWORD <span className="text-text-secondary normal-case">(if you sign in with a password)</span></label>
              <Input
                type="password"
                value={delPassword}
                onChange={(e) => setDelPassword(e.target.value)}
                placeholder="Your password"
                className="bg-surface-elevated border-white/10"
                data-testid="delete-password-input"
              />
            </div>
            <div>
              <label className="label-tiny block mb-1">TYPE <span className="text-red-400">DELETE MY ACCOUNT</span> TO CONFIRM</label>
              <Input
                value={delConfirm}
                onChange={(e) => setDelConfirm(e.target.value)}
                placeholder="DELETE MY ACCOUNT"
                className="bg-surface-elevated border-white/10"
                data-testid="delete-confirm-input"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setShowDelete(false)}
                data-testid="delete-cancel-button"
                className="flex-1 py-2.5 text-xs label-tiny tracking-widest border border-white/20 text-text-secondary hover:border-white/40 transition-colors"
              >
                CANCEL
              </button>
              <button
                onClick={deleteMyAccount}
                disabled={deleting || delConfirm.trim() !== "DELETE MY ACCOUNT"}
                data-testid="delete-confirm-button"
                className="flex-1 py-2.5 text-xs label-tiny tracking-widest bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-40"
              >
                {deleting ? "DELETING…" : "DELETE FOREVER"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile switch impact confirmation */}
      {confirmSwitch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="profile-confirm-modal">
          <div className="tactical-card p-6 max-w-md w-full space-y-4">
            <div className="flex items-start gap-3">
              <Warning size={24} weight="fill" className="text-heat flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-heading text-lg leading-tight">SWITCH TO {roleLabel(confirmSwitch.role).toUpperCase()}?</h3>
                <p className="text-xs text-text-secondary mt-1">Please review the impact before confirming.</p>
              </div>
            </div>
            <ul className="space-y-2">
              {confirmSwitch.notes.map((n, i) => (
                <li key={i} className="text-sm text-white/85 flex items-start gap-2">
                  <ShieldCheck size={15} weight="duotone" className="text-brand-accent flex-shrink-0 mt-0.5" />
                  <span>{n}</span>
                </li>
              ))}
            </ul>
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setConfirmSwitch(null)}
                data-testid="profile-confirm-cancel"
                className="flex-1 py-2.5 text-xs label-tiny tracking-widest border border-white/20 text-text-secondary hover:border-white/40 transition-colors"
              >
                CANCEL
              </button>
              <button
                onClick={() => applySwitch(confirmSwitch.role, true)}
                disabled={profileBusy}
                data-testid="profile-confirm-switch"
                className="flex-1 py-2.5 text-xs label-tiny tracking-widest btn-kinetic disabled:opacity-50"
              >
                {profileBusy ? "SWITCHING…" : "CONFIRM SWITCH"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
